import { useState, useCallback } from 'react';
import { ShieldCheck, Loader2, CheckCircle2, AlertCircle, AlertTriangle } from 'lucide-react';
import { api } from '@halo/web-lib';

interface StoreOption {
  id: number;
  name: string;
  integrations: string[];
}

interface CheckResult {
  label: string;
  status: 'ok' | 'warn' | 'error';
  detail: string;
}

interface QueueOverview {
  name: string;
  counts: { waiting: number; active: number; completed: number; failed: number; delayed: number };
}

export function HealthChecksPanel({ stores }: { stores: StoreOption[] }) {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<CheckResult[] | null>(null);

  const runChecks = useCallback(async () => {
    setRunning(true);
    setResults(null);
    const checks: CheckResult[] = [];

    // 1. Check queue health
    try {
      const res = await api.get<{ queues: QueueOverview[] }>('/api/v1/ingestion/admin/jobs');
      if (res.success && res.data) {
        const totalFailed = res.data.queues.reduce((s, q) => s + q.counts.failed, 0);
        const totalActive = res.data.queues.reduce((s, q) => s + q.counts.active, 0);
        if (totalFailed > 0) {
          checks.push({ label: 'Job Queues', status: 'warn', detail: `${totalFailed} failed jobs across queues` });
        } else {
          checks.push({ label: 'Job Queues', status: 'ok', detail: `${totalActive} active, 0 failed` });
        }
      }
    } catch {
      checks.push({ label: 'Job Queues', status: 'error', detail: 'Could not reach ingestion service' });
    }

    // 2. Check ingestion config health
    try {
      const res = await api.get<Array<{ status: string; lastScheduledAt: string | null; lastStoreActiveAt: string | null; lastBackfillAt: string | null }>>('/api/v1/ingestion/admin/configs');
      if (res.success && res.data) {
        const configs = res.data;
        const disabled = configs.filter((c) => c.status === 'disabled').length;
        const stale = configs.filter((c) => {
          if (c.status !== 'active') return false;
          const last = c.lastStoreActiveAt ?? c.lastScheduledAt ?? c.lastBackfillAt;
          if (!last) return true;
          return Date.now() - new Date(last).getTime() > 24 * 60 * 60 * 1000;
        }).length;

        if (disabled > 0 || stale > 0) {
          const parts = [];
          if (disabled > 0) parts.push(`${disabled} disabled`);
          if (stale > 0) parts.push(`${stale} stale (>24h)`);
          checks.push({ label: 'Ingestion Configs', status: 'warn', detail: parts.join(', ') });
        } else {
          checks.push({ label: 'Ingestion Configs', status: 'ok', detail: `${configs.length} configs all healthy` });
        }
      }
    } catch {
      checks.push({ label: 'Ingestion Configs', status: 'error', detail: 'Could not fetch configs' });
    }

    // 3. Verify data for first 3 stores with integrations
    const verifyStores = stores.filter((s) => s.integrations.length > 0).slice(0, 3);
    for (const store of verifyStores) {
      const platform = store.integrations[0]!;
      try {
        const endDate = new Date().toISOString().slice(0, 10);
        const start = new Date();
        start.setDate(start.getDate() - 7);
        const startDate = start.toISOString().slice(0, 10);

        const res = await api.post<{ healthy: boolean; coverage: string; coveredDays: number; expectedDays: number }>(
          '/api/v1/ingestion/admin/verify',
          { storeId: store.id, platform, startDate, endDate },
        );
        if (res.success && res.data) {
          if (res.data.healthy) {
            checks.push({ label: `Store #${store.id} (${platform})`, status: 'ok', detail: `${res.data.coverage} coverage, ${res.data.coveredDays}/${res.data.expectedDays} days` });
          } else {
            checks.push({ label: `Store #${store.id} (${platform})`, status: 'warn', detail: `${res.data.coverage} coverage — ${res.data.expectedDays - res.data.coveredDays} days missing` });
          }
        }
      } catch {
        checks.push({ label: `Store #${store.id} (${platform})`, status: 'error', detail: 'Verification failed' });
      }
    }

    // 4. Check services health
    try {
      const res = await api.get<{ services: Array<{ name: string; status: string }> }>('/health/services');
      if (res.success && res.data) {
        const unhealthy = res.data.services.filter((s) => s.status !== 'healthy');
        if (unhealthy.length > 0) {
          checks.push({ label: 'Services', status: 'error', detail: `${unhealthy.map((s) => s.name).join(', ')} unhealthy` });
        } else {
          checks.push({ label: 'Services', status: 'ok', detail: `All ${res.data.services.length} services healthy` });
        }
      }
    } catch {
      checks.push({ label: 'Services', status: 'ok', detail: 'Health endpoint not available (not critical)' });
    }

    setResults(checks);
    setRunning(false);
  }, [stores]);

  const okCount = results?.filter((r) => r.status === 'ok').length ?? 0;
  const warnCount = results?.filter((r) => r.status === 'warn').length ?? 0;
  const errCount = results?.filter((r) => r.status === 'error').length ?? 0;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Health Checks</span>
          {results && (
            <div className="flex items-center gap-1.5 ml-2">
              {okCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-400 font-medium">{okCount} ok</span>}
              {warnCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 font-medium">{warnCount} warn</span>}
              {errCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-400 font-medium">{errCount} err</span>}
            </div>
          )}
        </div>
        <button
          onClick={runChecks}
          disabled={running}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-900 dark:bg-gray-950 text-white hover:bg-gray-800 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />}
          {running ? 'Running...' : 'Run All Diagnostics'}
        </button>
      </div>

      {results && (
        <div className="space-y-1.5">
          {results.map((check, i) => {
            const StatusIcon = check.status === 'ok' ? CheckCircle2 : check.status === 'warn' ? AlertTriangle : AlertCircle;
            const color = check.status === 'ok' ? 'text-green-600 dark:text-green-400' : check.status === 'warn' ? 'text-amber-600' : 'text-red-500';
            const bg = check.status === 'ok' ? 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-900/50' : check.status === 'warn' ? 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/50' : 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900/50';
            return (
              <div key={i} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border ${bg}`}>
                <StatusIcon className={`w-3.5 h-3.5 shrink-0 ${color}`} />
                <span className="text-xs font-medium text-gray-800 dark:text-gray-100 w-40 truncate">{check.label}</span>
                <span className="text-[10px] text-gray-600 dark:text-gray-300 flex-1">{check.detail}</span>
              </div>
            );
          })}
        </div>
      )}

      {!results && !running && (
        <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-4">Click "Run All Diagnostics" to check system health across queues, configs, data coverage, and services.</p>
      )}
    </div>
  );
}
